import { type ApolloCache, useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import {
  ADD_PARTNER_CG_CUSTOM_DOMAIN_MUTATION,
  CHECK_CG_CUSTOM_DOMAIN_NOW_MUTATION,
  GET_CG_CUSTOM_DOMAINS_QUERY,
  REMOVE_CG_CUSTOM_DOMAIN_MUTATION,
  SET_CG_CUSTOM_DOMAIN_SEGMENTS_MUTATION,
} from 'src/graphql/entities/cg-custom-domain/cg-custom-domain.operations';
import {
  type CgCustomDomain,
  type GetCgCustomDomainsQueryData,
} from 'src/graphql/entities/cg-custom-domain/cg-custom-domain.types';

export const useManageCgCustomDomain = (partnerId: number | null) => {
  const updateDomains = useCallback(
    (cache: ApolloCache<unknown>, change: (domains: CgCustomDomain[]) => CgCustomDomain[]) => {
      cache.updateQuery<GetCgCustomDomainsQueryData>(
        {
          query: GET_CG_CUSTOM_DOMAINS_QUERY,
          variables: { input: { partnerId: partnerId ?? 0 } },
        },
        (current) => ({ getCgCustomDomains: change(current?.getCgCustomDomains ?? []) }),
      );
    },
    [partnerId],
  );

  const [addMutation, { loading: isAdding }] = useMutation<{
    addPartnerCgCustomDomain: CgCustomDomain;
  }>(ADD_PARTNER_CG_CUSTOM_DOMAIN_MUTATION);

  const [checkMutation] = useMutation<{
    checkCgCustomDomainNow: CgCustomDomain;
  }>(CHECK_CG_CUSTOM_DOMAIN_NOW_MUTATION);

  const [removeMutation] = useMutation(REMOVE_CG_CUSTOM_DOMAIN_MUTATION);

  const [setSegmentsMutation] = useMutation<{
    setCgCustomDomainSegments: CgCustomDomain;
  }>(SET_CG_CUSTOM_DOMAIN_SEGMENTS_MUTATION);

  const addDomain = useCallback(
    async (hostname: string) => {
      const { data } = await addMutation({
        variables: { input: { partnerId, hostname } },
        update: (cache, { data: added }) => {
          if (!added) return;

          updateDomains(cache, (domains) => [...domains, added.addPartnerCgCustomDomain]);
        },
      });

      return data?.addPartnerCgCustomDomain ?? null;
    },
    [addMutation, partnerId, updateDomains],
  );

  const setSegments = useCallback(
    async (domain: CgCustomDomain, brandIds: string[]) => {
      const { partnerCustomDomainId } = domain;

      await setSegmentsMutation({
        variables: { input: { partnerId, partnerCustomDomainId, brandIds } },
        // Applied at once, so a second toggle before this returns builds on the first.
        optimisticResponse: { setCgCustomDomainSegments: { ...domain, brandIds } },
        update: (cache, { data }) => {
          if (!data) return;

          const updated = data.setCgCustomDomainSegments;

          // A segment moved here leaves whichever domain it was on.
          updateDomains(cache, (domains) =>
            domains.map((other) =>
              other.partnerCustomDomainId === partnerCustomDomainId
                ? updated
                : {
                    ...other,
                    brandIds: other.brandIds.filter((id) => !updated.brandIds.includes(id)),
                  },
            ),
          );
        },
      });
    },
    [setSegmentsMutation, partnerId, updateDomains],
  );

  const checkNow = useCallback(
    async (partnerCustomDomainId: string) => {
      const { data } = await checkMutation({
        variables: { input: { partnerId, partnerCustomDomainId } },
        update: (cache, { data: checked }) => {
          if (!checked) return;

          updateDomains(cache, (domains) =>
            domains.map((domain) =>
              domain.partnerCustomDomainId === partnerCustomDomainId
                ? checked.checkCgCustomDomainNow
                : domain,
            ),
          );
        },
      });

      return data?.checkCgCustomDomainNow ?? null;
    },
    [checkMutation, partnerId, updateDomains],
  );

  const removeDomain = useCallback(
    async (partnerCustomDomainId: string) => {
      await removeMutation({
        variables: { input: { partnerId, partnerCustomDomainId } },
        update: (cache) =>
          updateDomains(cache, (domains) =>
            domains.filter((domain) => domain.partnerCustomDomainId !== partnerCustomDomainId),
          ),
      });
    },
    [removeMutation, partnerId, updateDomains],
  );

  return { addDomain, setSegments, checkNow, removeDomain, isAdding };
};
